import { DateRange } from "@angular/material/datepicker";
import { DateUtil } from "@core/utils";
import { DatePreset } from "@core/common";
import { PeriodOption } from "./date.model";

export const DEFAULT_VISIBLE_OPTIONS_COUNT = 20 as const;

export const DEFAULT_DATE_RANGE = new DateRange(
  new DateUtil().offset(-1, 'month').startOfMonth().startOfDay().toDate(),
  new DateUtil().endOfDay().toDate(),
);

export function isPeriodOption(option: PeriodOption): boolean {
  return option.value?.includes(' - ');
}

export function dateRangeToOption(range: DateRange<Date>): PeriodOption {
  if (!range.start || !range.end) {
    throw new Error('Invalid date range');
  }

  const label = buildDateLabel(range.start, range.end);

  return {
    label: label,
    value: label,
  };
}

export function dateRangeFromOption(option?: PeriodOption): DateRange<Date | null> {
  if (!option) {
    return new DateRange(null, null);
  }

  const { start, end } = parseDateLabel(option.value);

  return new DateRange(start || DEFAULT_DATE_RANGE.start, end || DEFAULT_DATE_RANGE.end);
}

export function buildDateLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  const monthShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' });
  const day = (d: Date) => d.getDate();
  const year = (d: Date) => d.getFullYear();

  if (sameMonth) {
    // Jan 1 - 15, 2026
    return `${monthShort(start)} ${day(start)} - ${day(end)}, ${year(end)}`;
  }

  if (sameYear) {
    // Jan 1 - Feb 15, 2026
    return `${monthShort(start)} ${day(start)} - ${monthShort(end)} ${day(end)}, ${year(end)}`;
  }

  // Jan 1, 2025 - Feb 15, 2026
  return `${monthShort(start)} ${day(start)}, ${year(start)} - ${monthShort(end)} ${day(end)}, ${year(end)}`;
}

export function parseDateLabel(label: string): DateRange<Date> {
  // Case: Jan 1, 2025 - Feb 15, 2026
  const diffYearRegex = /^([A-Za-z]+)\s(\d{1,2}),\s(\d{4})\s-\s([A-Za-z]+)\s(\d{1,2}),\s(\d{4})$/;

  // Case: Jan 1 - Feb 15, 2026
  const sameYearRegex = /^([A-Za-z]+)\s(\d{1,2})\s-\s([A-Za-z]+)\s(\d{1,2}),\s(\d{4})$/;

  // Case: Jan 1 - 15, 2026
  const sameMonthRegex = /^([A-Za-z]+)\s(\d{1,2})\s-\s(\d{1,2}),\s(\d{4})$/;

  const getMonthIndex = (shortName: string): number => {
    const months: { [key: string]: number } = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    const index = months[shortName];
    if (index === undefined) throw new Error(`Invalid month name: ${shortName}`);
    return index;
  };

  let match: RegExpMatchArray | null;

  // match different years
  if ((match = label.match(diffYearRegex))) {
    const [, startMonth, startDay, startYear, endMonth, endDay, endYear] = match;
    return new DateRange(
      new Date(+startYear, getMonthIndex(startMonth), +startDay),
      new Date(+endYear, getMonthIndex(endMonth), +endDay)
    );
  }

  // match same year
  if ((match = label.match(sameYearRegex))) {
    const [, startMonth, startDay, endMonth, endDay, year] = match;
    return new DateRange(
      new Date(+year, getMonthIndex(startMonth), +startDay),
      new Date(+year, getMonthIndex(endMonth), +endDay)
    );
  }

  // match same month
  if ((match = label.match(sameMonthRegex))) {
    const [, month, startDay, endDay, year] = match;
    const monthIndex = getMonthIndex(month);
    return new DateRange(
      new Date(+year, monthIndex, +startDay),
      new Date(+year, monthIndex, +endDay)
    );
  }

  throw new Error(`Label format not recognized: ${label}`);
}

export const excludeOptions = (option: PeriodOption) => {
  const currentQuarter = DateUtil.getCurrentQuarter();

  if (option.value === DatePreset.Q1 && currentQuarter < 2) return false;
  if (option.value === DatePreset.Q2 && currentQuarter < 3) return false;
  if (option.value === DatePreset.Q3 && currentQuarter < 4) return false;
  if (option.value === DatePreset.Q4 && currentQuarter < 5) return false;

  return true;
};  